import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { OtpInput } from "components/ui/otp-input";
import routes from "routes/routes";
import { useAppDispatch } from "../../../redux/useDispatch";
import {
  accountApiSlice,
  useDeleteAccountMutation,
  useRequestAccountDeleteMutation,
} from "api/account/accountApiSlice";
import { AccountApiError } from "api/account/types";
import { clearAccount } from "../../../redux/slice/account/accountSlice";
import { clearAccountSessionToken } from "authentication/accountSession";
import { clearBookingPersonalData } from "util/clearBookingPersonalData";
import ConfirmDialog from "../../common/ConfirmDialog";

/** OTP-failure codes on delete that mean "the code is no longer usable". */
const EXPIRED_ERROR_CODES = ["expired", "too_many_attempts"];

/**
 * H-3a F2 — GDPR account deletion, OTP-verified. Bookings stay (they are
 * business records); only the account itself is removed. On success the local
 * session is torn down exactly like a logout and the user lands on home.
 */
const DeleteAccountSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [requestAccountDelete, { isLoading: isRequesting }] =
    useRequestAccountDeleteMutation();
  const [deleteAccount, { isLoading: isDeleting }] = useDeleteAccountMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  const handleConfirmDialog = async () => {
    setDialogOpen(false);
    setRequestError(null);
    try {
      await requestAccountDelete().unwrap();
      setCodeSent(true);
      setCode("");
      setCodeError(null);
    } catch {
      setRequestError(t("account.login.error_send_failed"));
    }
  };

  const handleCodeChange = (nextValue: string) => {
    setCode(nextValue);
    setCodeError(null);
  };

  const handleDelete = async () => {
    if (code.length !== 6) {
      setCodeError(t("account.verify.error_invalid_length"));
      return;
    }

    try {
      await deleteAccount({ code }).unwrap();
      clearAccountSessionToken();
      dispatch(clearAccount());
      // Drop every cached /me result — the account no longer exists.
      dispatch(accountApiSlice.util.resetApiState());
      // GDPR delete is a session end: the booking form must not keep the
      // deleted account's PII (redux + IDB draft) — #1873 scenario 4.
      clearBookingPersonalData(dispatch);
      navigate(routes.home);
    } catch (error) {
      const apiError = error as AccountApiError;
      const errorCode = apiError?.data?.code?.toLowerCase();
      setCodeError(
        errorCode !== undefined && EXPIRED_ERROR_CODES.includes(errorCode)
          ? t("account.verify.error_expired")
          : t("account.verify.error_wrong_code")
      );
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-medium text-slate-700">
        {t("account.delete.title")}
      </h2>
      <p className="text-sm text-slate-600">
        {t("account.delete.description")}
      </p>

      {requestError && <p className="text-sm text-red-600">{requestError}</p>}

      {!codeSent && (
        <Button
          size="lg"
          variant="outline"
          className="w-full"
          disabled={isRequesting}
          onClick={() => setDialogOpen(true)}
        >
          {isRequesting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              {t("account.login.sending")}
            </>
          ) : (
            t("account.delete.button")
          )}
        </Button>
      )}

      {codeSent && (
        <div className="space-y-2">
          <p className="text-center text-sm text-slate-600">
            {t("account.delete.code_sent")}
          </p>
          <OtpInput
            value={code}
            onChange={handleCodeChange}
            length={6}
            disabled={isDeleting}
            error={Boolean(codeError)}
            autoFocus
            className="justify-center"
          />
          {codeError && (
            <p className="text-center text-sm text-red-600">{codeError}</p>
          )}
          <Button
            size="lg"
            variant="primary"
            className="w-full"
            disabled={isDeleting || code.length !== 6}
            onClick={handleDelete}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t("account.delete.deleting")}
              </>
            ) : (
              t("account.delete.confirm")
            )}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={dialogOpen}
        title={t("account.delete.dialog_title")}
        content={t("account.delete.dialog_content")}
        cancelText={t("account.delete.dialog_cancel")}
        confirmText={t("account.delete.dialog_confirm")}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirmDialog}
      />
    </div>
  );
};

export default DeleteAccountSection;
